import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { CanLeadInvestmentsOptionNames } from 'activities/auth/OnboardingProfileActivity/dataService'; // TODO: directory path
import {
  InvestorsTabPaneDocument,
  useSaveUserProfileMutation as useBaseMutation,
  useEditUserModalQuery as useBaseQuery
} from 'generated/graphql';
import _ from 'lodash';
import {
  useLoading,
  validationUtil
} from 'modules';
import {
  fileData2EncodedString,
  imageUtil
} from 'modules';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';



const __formSchema = yup.object({
  profilePictureFile: validationUtil.fileData.nullable().default(undefined),
  firstName: yup.string(),
  lastName: yup.string(),
  companyName: yup.string().required('Company name is required.'),
  companyDescription: yup.string().max(250),
  websiteUrl: validationUtil.websiteUrlSchema,
  email: validationUtil.emailSchema,
  phoneNumber: yup.string(),
  investmentStages: yup.array().of(validationUtil.investmentStageSchema.required()),
  investmentAreas: yup.array().of(yup.string().uuid().required()),
  investmentCategory: validationUtil.investmentCategorySchema,
  investmentGeographies: yup.array().of(validationUtil.investmentGeographySchema.required()),
  checkSizeMinDollars: validationUtil.positiveIntegerSchema.nullable(),
  checkSizeMaxDollars: validationUtil.positiveIntegerSchema.nullable().min(yup.ref('checkSizeMinDollars'), 'Must be no less than minimum value'),
  canLeadInvestments: yup.mixed<CanLeadInvestmentsOptionNames>(),
  city: yup.string().required('City is required.'),
  usState: yup.string().nullable(true),
  country: yup.string().required('Country is required.')
}).required();

export type IFormData = yup.InferType<typeof __formSchema>;



interface UseSaveUserProfileArgs {
  siteUserId: string;
  companyProfileId: string;
  investorProfileId?: string;
}

export function useSaveUserProfile(args: UseSaveUserProfileArgs) {
  const tuple = useBaseQuery({
    variables: {
      id: args.siteUserId,
      isInvestor: !!args.investorProfileId
    }
  });

  const [saveUserProfile, saveUserProfileTuple] = __useSaveUserProfileMutation(args);

  const user = tuple.data?.user;
  const profilePictureUrl = user?.companyProfile?.logo?.key ? imageUtil.getFileUrl(user?.companyProfile?.logo?.key) : undefined;
  const companyProfile = tuple.data?.user?.companyProfile;
  const investorProfile = tuple.data?.user?.investorProfile;

  const loading = useLoading([tuple, saveUserProfileTuple]);

  const data = React.useMemo(() => ({
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    email: user?.email ?? '',
    companyName: companyProfile?.name,
    companyDescription: companyProfile?.description,
    phoneNumber: companyProfile?.phoneNumber,
    websiteUrl: companyProfile?.websiteUrl,
    investmentStages: companyProfile?.investmentStages ? _.map(companyProfile.investmentStages, stage => stage.name) : [],
    investmentCategory: companyProfile?.investmentCategory?.name,
    investmentAreas: companyProfile?.investmentAreas ? _.map(companyProfile.investmentAreas, area => area.id): [],
    investmentGeographies: companyProfile?.investmentGeographies ? _.map(companyProfile.investmentGeographies, geo => geo.name) : [],
    checkSizeMinDollars: investorProfile?.checkSizeMinDollars,
    checkSizeMaxDollars: investorProfile?.checkSizeMaxDollars,
    canLeadInvestments: investorProfile?.canLeadInvestments === false ? 'no' : 'yes' as CanLeadInvestmentsOptionNames,
    city: companyProfile?.address.city,
    usState: companyProfile?.address.usState,
    country: companyProfile?.address.country
  }), [user, companyProfile, investorProfile]);

  const form = useForm<IFormData>({
    resolver: yupResolver(__formSchema)
  });

  React.useEffect(() => {
    const isEmpty = _.every(data, d => _.isEmpty(d));

    if (!isEmpty) {
      form.reset(data);
    }
  }, [form, data]);

  return {
    data: {
      profilePictureUrl
    },
    form,
    loading,
    mutation: {
      saveUserProfile
    }
  };
}



function __useSaveUserProfileMutation(args: UseSaveUserProfileArgs) {
  const [fnMutate, tuple] = useBaseMutation({
    refetchQueries: [
      InvestorsTabPaneDocument,
      'InvestorsTabPane'
    ]
  });

  async function fnWrappedMutate(formData: IFormData) {
    await fnMutate({
      variables: {
        userInput: {
          id: args.siteUserId,
          firstName: formData.firstName,
          lastName: formData.lastName
        },

        companyProfileInput: {
          id: args.companyProfileId,
          name: formData.companyName,
          logo: formData.profilePictureFile ? fileData2EncodedString(formData.profilePictureFile) : undefined,
          websiteUrl: formData.websiteUrl,
          description: formData.companyDescription,
          phoneNumber: formData.phoneNumber,
          investmentStages: formData.investmentStages,
          investmentCategory: formData.investmentCategory,
          investmentAreas: formData.investmentAreas,
          address: {
            city: formData.city ?? '',
            usState: formData.usState,
            country: formData.country ?? ''
          }
        },

        isInvestor: !!args.investorProfileId,
        investorProfile: {
          id: args.investorProfileId ?? '',
          checkSizeMinDollars: formData.checkSizeMinDollars,
          checkSizeMaxDollars: formData.checkSizeMaxDollars,
          canLeadInvestments: formData.canLeadInvestments === 'yes'
        }
      }
    });
  }

  return [fnWrappedMutate, tuple] as const;
}
